import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, IconComponent],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalComponent {
  open = input(false);
  title = input('');
  confirmLabel = input('Save');
  cancelLabel = input('Cancel');
  confirmVariant = input('primary');
  showFooter = input(true);
  showCancel = input(true);
  confirmDisabled = input(false);
  closeOnBackdrop = input(true);
  showCloseButton = input(true);
  size = input<'sm' | 'md' | 'lg'>('md');

  closed = output<void>();
  confirmed = output<void>();

  readonly modalTemplate = viewChild.required<TemplateRef<unknown>>('modalTemplate');

  private readonly document = inject(DOCUMENT);
  private readonly vcr = inject(ViewContainerRef);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private portalOutlet: DomPortalOutlet | null = null;
  private portalHost: HTMLElement | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;
  private boundKeyHandler = this.onKeydown.bind(this);

  constructor() {
    effect(() => {
      if (this.open()) {
        this.attachToBody();
      } else {
        this.detachFromBody();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.detachFromBody();
    });
  }

  onOverlayClick(e: MouseEvent): void {
    if (e.target === e.currentTarget && this.closeOnBackdrop()) this.closed.emit();
  }

  protected get confirmColor(): string {
    const v = this.confirmVariant();
    if (v === 'danger') return 'warn';
    if (v === 'success' || v === 'secondary') return 'accent';
    return 'primary';
  }

  private attachToBody(): void {
    if (this.portalOutlet) return;

    this.previouslyFocusedElement = this.document.activeElement as HTMLElement;

    // Create a host element appended to <body>
    this.portalHost = this.document.createElement('div');
    this.portalHost.classList.add('app-modal-host');
    this.document.body.appendChild(this.portalHost);

    // Create portal outlet and attach the template
    this.portalOutlet = new DomPortalOutlet(this.portalHost, this.appRef, this.injector);
    const portal = new TemplatePortal(this.modalTemplate(), this.vcr);
    this.portalOutlet.attach(portal);

    // Block body scroll and listen for keyboard
    this.document.body.style.overflow = 'hidden';
    this.document.addEventListener('keydown', this.boundKeyHandler);

    // Focus the title (not the first focusable element — that's the close
    // button, and focusing it programmatically still shows Material's focus
    // ring even though the user never touched it). A plain heading needs no
    // visible focus indicator, so nothing looks "hovered" the instant the
    // modal opens.
    requestAnimationFrame(() => {
      const modal = this.portalHost?.querySelector('.modal') as HTMLElement;
      const titleEl = modal?.querySelector<HTMLElement>('.modal-title');
      titleEl?.focus();
    });
  }

  private detachFromBody(): void {
    if (!this.portalOutlet) return;

    this.document.removeEventListener('keydown', this.boundKeyHandler);
    this.document.body.style.overflow = '';

    this.portalOutlet.dispose();
    this.portalOutlet = null;

    if (this.portalHost) {
      this.portalHost.remove();
      this.portalHost = null;
    }

    this.previouslyFocusedElement?.focus();
    this.previouslyFocusedElement = null;
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.closeOnBackdrop()) this.closed.emit();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    const modal = this.portalHost?.querySelector('.modal') as HTMLElement;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (this.document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (this.document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }
}
